/**
 * Pure helpers behind the synchronized chart cursor and the per-chart Max/Avg
 * summaries shown by `TimeseriesCard`. Kept free of React/recharts so they can be
 * unit tested directly.
 */

export interface StatsPoint {
  timestamp: number;
  value: number;
}

export interface SeriesWindowStats {
  /** Highest sample inside the window, or null when the window holds no samples. */
  max: number | null;
  /** Arithmetic mean of the samples inside the window, or null when there are none. */
  avg: number | null;
}

/** Linear interpolation of a series value at `tMs`, clamped to the first/last sample. */
export function interpolateAtTimestamp(points: readonly StatsPoint[], tMs: number): number | null {
  if (points.length === 0 || !Number.isFinite(tMs)) return null;
  const sorted = [...points].sort((a, b) => a.timestamp - b.timestamp);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (tMs <= first.timestamp) return first.value;
  if (tMs >= last.timestamp) return last.value;
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (tMs >= a.timestamp && tMs <= b.timestamp) {
      const span = b.timestamp - a.timestamp;
      if (span === 0) return a.value;
      const r = (tMs - a.timestamp) / span;
      return a.value + r * (b.value - a.value);
    }
  }
  return null;
}

/**
 * Max and average of the samples that fall inside `xDomain` (inclusive). Non-finite
 * samples are ignored so a single NaN reading cannot poison the whole summary.
 * Without an `xDomain` the whole series is summarized.
 */
export function computeSeriesWindowStats(
  points: readonly StatsPoint[],
  xDomain?: readonly [number, number]
): SeriesWindowStats {
  let max: number | null = null;
  let sum = 0;
  let count = 0;

  for (const p of points) {
    if (!Number.isFinite(p.value)) continue;
    if (xDomain && (p.timestamp < xDomain[0] || p.timestamp > xDomain[1])) continue;
    if (max === null || p.value > max) max = p.value;
    sum += p.value;
    count += 1;
  }

  return { max, avg: count > 0 ? sum / count : null };
}

/** Shape of the chart state recharts hands to a categorical chart's `onClick`. */
export interface ChartClickState {
  activeLabel?: unknown;
  isTooltipActive?: boolean;
}

/**
 * Timestamp (ms) of the point a chart click landed on, or null when the click did
 * not resolve to a data point (e.g. a click on empty canvas outside the plot area).
 */
export function resolveChartClickTimestamp(state: ChartClickState | null | undefined): number | null {
  if (!state) return null;
  const raw = state.activeLabel;
  const ms = typeof raw === 'string' ? Number(raw) : raw;
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return null;
  return ms;
}
